import { useQueryClient } from '@tanstack/react-query';
import { useRetrieveListByGet } from 'src/compass/shared/hooks/reactQuery/useRetrieveListByGet';

const SITE_PROPERTIES_URL = 'Public/Properties/Site';

// The columns of web.FeatureFlags, edited on Administration > Configuration > Feature Flags.
export interface IFeatureFlags {
  showBatchSend: boolean;
  showClaimDueReminders: boolean;
  showCustomerSend: boolean;
}

// Site properties are cached for the whole session (staleTime: Infinity), so anything that
// changes them - saving the feature flags - has to invalidate them to be seen without a reload.
export const useSitePropertiesInvalidateQuery = () => {
  const queryClient = useQueryClient();

  return () => {
    queryClient.invalidateQueries({ queryKey: [SITE_PROPERTIES_URL] });
  };
};

export default function useSiteProperties() {
  const query = useRetrieveListByGet({
    url: SITE_PROPERTIES_URL,
    staleTime: Infinity,
  });

  const databaseName = query.data?.databaseName?.toLowerCase();

  const isProductionDatabase = !databaseName || databaseName === 'production';

  // Both are empty unless the API is running against a provisioned environment's
  // database - the backend resolves that from the catalog it is connected to,
  // so the frontend does no environment lookup of its own.
  const documentation: string = query.data?.documentation ?? '';

  // A path relative to the API base (e.g. "Public/Properties/SiteAttachment"),
  // so it has to be resolved against REACT_APP_API_URL - which already ends in
  // "/api/" - to be usable as an href.
  const attachmentPath: string = query.data?.attachmentUrl ?? '';
  const attachmentUrl = attachmentPath
    ? `${process.env.REACT_APP_API_URL}${attachmentPath}`
    : '';

  // The name that download saves as, resolved server-side - so the dialog can
  // label the link with the filename the user will end up with.
  const attachmentFilename: string = query.data?.attachmentFilename ?? '';

  // Whether there is anything for the header's environment dialog to show. When
  // false the header tag stays a plain label, which is the case on dev2,
  // production and ordinary local dev.
  const hasEnvironmentInfo = documentation.trim() !== '' || attachmentUrl !== '';

  // Empty until the properties load, so every flag reads as off (feature hidden) until then.
  const featureFlags: Partial<IFeatureFlags> = query.data?.featureFlags ?? {};

  return {
    ...query,
    isProductionDatabase,
    documentation,
    attachmentUrl,
    attachmentFilename,
    hasEnvironmentInfo,
    featureFlags,
  };
}
